import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const CartCtx = createContext(null);

export const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const STORAGE_KEY = "sc_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((name, label, price) => {
    const id = slugify(`${name} ${label}`);
    setItems((o) => ({
      ...o,
      [id]: { id, name, label, price: parseFloat(price), qty: (o[id]?.qty || 0) + 1 },
    }));
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((o) => {
      if (!o[id]) return o;
      const next = { ...o };
      if (qty <= 0) delete next[id];
      else next[id] = { ...o[id], qty };
      return next;
    });
  }, []);

  const clear = useCallback(() => setItems({}), []);
  const openCheckout = useCallback(() => setOpen(true), []);
  const closeCheckout = useCallback(() => setOpen(false), []);

  const lines = useMemo(() => Object.values(items), [items]);
  const count = lines.reduce((s, v) => s + v.qty, 0);
  const subtotal = lines.reduce((s, v) => s + v.qty * v.price, 0);

  const value = { items, lines, count, subtotal, add, setQty, clear, open, openCheckout, closeCheckout };
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => useContext(CartCtx);
